import { useLayoutEffect, useMemo, useState } from 'react'
import {
  Box,
  Stack,
  styled,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  WappStage,
  useWappStage,
  WapprunnerCreate,
} from 'weblite-web-wapprunner'
import { WappMode } from '@weblite-wapps/protocol'
import { useHistoryCustomBack } from 'weblite-router'
import { CloseIcon } from '$common/icons'
import { getWappTheme } from '$common/wapp-bar-theme'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { useShowCallBar } from '$logic/call'
import { useWapp } from '$logic/wapps/react-hook'
import { WappPanelHeader } from './components/wapp-panel-header'
import { useCurrentWappMeta } from './react-hooks'
import { WappPanel as WappPanelClass } from '$logic/wapp-panels'
import { NativeColors, useSetNativeColors } from '$logic/native-colors'
import { getWappIcon } from '$logic/wapps/wapps.helper'

const LoadingBox = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;

  width: 100%;
  height: 100%;
`

const LogoBox = styled(Stack)`
  position: absolute;
  left: 0;
  right: 0;
  top: 35%;

  align-items: center;
  justify-content: center;

  // wapp logo
  & > svg,
  & > img {
    width: 80px;
    height: 80px;
  }

  & > img {
    border-radius: 16px 16px 16px 0;
  }

  & path {
    fill: #fff;
  }
`

const LoadingProgress = styled(CircularProgress)`
  color: #fff;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 80px;
  margin: auto;
`

export const wappPanelAPI = {
  //  eslint-disable-next-line @typescript-eslint/no-unused-vars
  setNativeColors(_: NativeColors) {},
}

export function WappPanel() {
  const currentWappMeta = useCurrentWappMeta()
  const {
    wappSessionId,
    wappId,
    wisId,
    input,
    hideHeader: runtimeHideHeader,
    parent,
  } = currentWappMeta || {}

  const [overriddenNativeColors, setOverriddenNativeColors] = useState<
    NativeColors
  >({})

  useLayoutEffect(() => {
    wappPanelAPI.setNativeColors = (newColors: NativeColors) =>
      setOverriddenNativeColors(colors => ({
        statusBar: newColors.statusBar || colors.statusBar,
        bottomSafeArea: newColors.bottomSafeArea || colors.bottomSafeArea,
      }))
  }, [])

  useSetNativeColors(
    () => overriddenNativeColors,
    [overriddenNativeColors],
    'wappSessionId' in (currentWappMeta || {}),
  )

  const { entity: wapp, loading } = useWapp(wappId) || {}
  // @ts-ignore
  const hideHeader = Boolean(wapp?.hideHeader || runtimeHideHeader)

  const wappStage = useWappStage(currentWappMeta?.wappSessionId)
  const showCallBar = useShowCallBar()

  useHistoryCustomBack(() => WappPanelClass.popFromPanel(), [])

  const wappName = wapp?.groupName
  const {
    name: nameInTheme,
    icon: iconInTheme,
    loadingGradient,
  } = getWappTheme(wappId)

  const wappIcon = useMemo(
    () => iconInTheme || <img src={getWappIcon(wappId)} alt="Wapp Icon" />,
    [wappId, iconInTheme],
  )

  if (!currentWappMeta || !('wappSessionId' in currentWappMeta)) return null

  const isLoading = wappStage === WappStage.LOADING
  return (
    <PageWithAppBar
      appBar={
        <>
          {showCallBar && <CallBar />}
          {!isLoading && !loading && !hideHeader && (
            <WappPanelHeader wappId={wappId} wappSessionId={wappSessionId} />
          )}
        </>
      }
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        <WapprunnerCreate
          key={wappSessionId}
          wappSessionId={wappSessionId}
          wappId={wappId}
          wisId={wisId}
          input={input}
          parentWapp={parent}
          mode={WappMode.MAIN}
        />

        {isLoading && (
          <LoadingBox sx={{ background: loadingGradient }}>
            <IconButton
              sx={{ position: 'absolute', left: '15px', top: '15px' }}
              onClick={() => WappPanelClass.closeTopWapp()}
            >
              <CloseIcon />
            </IconButton>

            <LogoBox>
              {wappIcon}

              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: loadingGradient === 'unset' ? 'neutral600' : '#fff',
                  marginTop: '20px',
                }}
              >
                {iconInTheme ? nameInTheme : wappName}
              </Typography>
            </LogoBox>

            <LoadingProgress />
          </LoadingBox>
        )}
      </Box>
    </PageWithAppBar>
  )
}
